import { useEffect, useMemo, useState } from "react";
import { api, formatCurrencyEUR } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";
import YieldChart from "./YieldChart";
import AssetAllocation from "./AssetAllocation";

const MIN = 25000;
const MAX = 100000;
const STEP = 1000;
const DEFAULT_AMOUNT = 50000;

/**
 * Yield calculator — visible on the dashboard for "initial" clients
 * (no active deposits). Pulls live plan tiers from /api/plans/calculator
 * and resolves the matching tier as the slider moves.
 */
export default function YieldCalculatorCard() {
  const { t, locale } = useI18n();
  const [eligible, setEligible] = useState(false);
  const [plans, setPlans] = useState([]);
  const [amount, setAmount] = useState(DEFAULT_AMOUNT);
  const [duration, setDuration] = useState("12");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/plans/calculator")
      .then(({ data }) => {
        if (!alive) return;
        setEligible(Boolean(data.eligible));
        setPlans(Array.isArray(data.plans) ? data.plans : []);
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  // Available durations (sorted: 12, 24, 36, open)
  const availableDurations = useMemo(() => {
    const set = new Set(plans.map((p) => p.duration));
    const order = ["12", "24", "36", "open"];
    return order.filter((d) => set.has(d));
  }, [plans]);

  // Auto-reset duration if not present in the catalogue
  useEffect(() => {
    if (availableDurations.length && !availableDurations.includes(duration)) {
      setDuration(availableDurations[0]);
    }
  }, [availableDurations, duration]);

  const tiersForDuration = useMemo(
    () =>
      plans
        .filter((p) => p.duration === duration)
        .sort((a, b) => a.min_amount - b.min_amount),
    [plans, duration],
  );

  const activeTier = useMemo(() => {
    if (!tiersForDuration.length) return null;
    const descending = [...tiersForDuration].sort(
      (a, b) => b.min_amount - a.min_amount,
    );
    for (const tier of descending) {
      if (amount >= tier.min_amount) return tier;
    }
    return descending[descending.length - 1];
  }, [amount, tiersForDuration]);

  // Months for the projection: 12/24/36 use the literal; "open" projects 12m.
  const projectionMonths = useMemo(() => {
    if (duration === "open") return 12;
    return Number(duration) || 12;
  }, [duration]);

  const calc = useMemo(() => {
    if (!activeTier) return null;
    const years = projectionMonths / 12;
    const gross = (amount * activeTier.gross_rate * years) / 100;
    const mngmt = (amount * activeTier.mngmt_fee * years) / 100;
    const perf = (gross * activeTier.perf_fee) / 100;
    const net = gross - mngmt - perf;
    const netRate = (net / amount / years) * 100;
    return {
      grossProfit: gross,
      mngmtCost: mngmt,
      perfCost: perf,
      netProfit: net,
      netRate,
      maturity: amount + net,
    };
  }, [amount, activeTier, projectionMonths]);

  if (!loaded || !eligible || tiersForDuration.length === 0) return null;

  const pct = ((amount - MIN) / (MAX - MIN)) * 100;
  const lockLabel = activeTier?.lock_period_months
    ? `${activeTier.lock_period_months} ${t("calc.months")}`
    : t("calc.lock_open");

  return (
    <section
      className="mt-14 br-card overflow-hidden br-fade-up br-delay-2"
      data-testid="yield-calc-card"
      style={{ position: "relative" }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 80% 0%, rgba(167,128,73,0.10), transparent 55%)",
          pointerEvents: "none",
        }}
      />

      <div className="relative px-8 pt-9 pb-3">
        <div className="br-overline" data-testid="calc-overline">
          {t("calc.overline")}
        </div>
        <h2
          className="font-serif-display text-[2rem] mt-3 leading-tight"
          data-testid="calc-title"
        >
          {t("calc.title")}
        </h2>
        <p className="text-[0.92rem] text-[color:var(--br-text-dim)] mt-3 max-w-[640px] leading-relaxed">
          {t("calc.intro")}
        </p>
      </div>

      {/* Duration picker pills */}
      <div className="relative px-8 pt-3 pb-2">
        <div className="br-overline mb-3">{t("calc.duration_label")}</div>
        <div className="flex flex-wrap gap-2" data-testid="calc-duration-picker">
          {availableDurations.map((d) => {
            const active = d === duration;
            return (
              <button
                key={d}
                type="button"
                onClick={() => setDuration(d)}
                className="font-mono-fin text-[0.7rem] tracking-[0.2em] uppercase border px-4 py-2 transition-colors"
                style={{
                  color: active ? "var(--br-bronze)" : "var(--br-text-dim)",
                  borderColor: active
                    ? "var(--br-bronze)"
                    : "var(--br-line-bright)",
                  background: active ? "rgba(167,128,73,0.06)" : "transparent",
                }}
                data-testid={`calc-duration-${d}`}
              >
                {t(`calc.duration_${d}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Amount slider */}
      <div className="relative px-8 pt-6 pb-2">
        <div className="flex items-baseline justify-between mb-2">
          <span className="br-overline">{t("calc.amount_label")}</span>
          <span
            className="font-serif-display text-[2.2rem]"
            style={{ color: "var(--br-bronze)" }}
            data-testid="calc-amount-display"
          >
            {formatCurrencyEUR(amount, locale)}
          </span>
        </div>
        <div className="relative" style={{ height: "44px" }}>
          <div
            className="absolute left-0 right-0"
            style={{
              top: "20px",
              height: "2px",
              background: "var(--br-line-bright)",
            }}
          />
          <div
            className="absolute left-0"
            style={{
              top: "20px",
              height: "2px",
              width: `${pct}%`,
              background: "var(--br-bronze)",
              transition: "width 120ms ease-out",
            }}
          />
          <input
            type="range"
            min={MIN}
            max={MAX}
            step={STEP}
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            className="absolute inset-0 w-full opacity-0 cursor-pointer"
            data-testid="calc-amount-slider"
          />
          <div
            className="absolute pointer-events-none"
            style={{
              left: `calc(${pct}% - 9px)`,
              top: "12px",
              width: "18px",
              height: "18px",
              borderRadius: "50%",
              background: "var(--br-bronze)",
              boxShadow: "0 0 0 4px rgba(167,128,73,0.15)",
              transition: "left 120ms ease-out",
            }}
          />
        </div>
        <div className="flex justify-between mt-1 font-mono-fin text-[0.7rem] text-[color:var(--br-text-dim)]">
          <span>{formatCurrencyEUR(MIN, locale)}</span>
          <span>{formatCurrencyEUR(MAX, locale)}</span>
        </div>
      </div>

      {/* Tier + numbers */}
      {activeTier && calc ? (
        <div className="relative px-8 pt-6 pb-8">
          <div
            className="br-card p-5 mb-5"
            style={{ borderLeft: "3px solid var(--br-bronze)" }}
            data-testid="calc-tier-card"
          >
            <div
              className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase"
              style={{ color: "var(--br-bronze)" }}
            >
              {t("calc.tier_label")}
            </div>
            <div
              className="font-serif-display text-[1.25rem] mt-1 text-[color:var(--br-parchment)]"
              data-testid="calc-tier-name"
            >
              {activeTier.name}
            </div>
            {activeTier.description && (
              <div className="text-[0.82rem] text-[color:var(--br-text-dim)] mt-1">
                {activeTier.description}
              </div>
            )}
          </div>

          {/* SVG growth chart */}
          <div
            className="br-card px-5 py-5 mb-5"
            style={{ borderTop: "1px solid var(--br-line)" }}
            data-testid="calc-chart-wrapper"
          >
            <div className="flex items-baseline justify-between mb-3">
              <div
                className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase"
                style={{ color: "var(--br-bronze)" }}
              >
                {t("calc.chart_title")}
              </div>
              <div className="font-mono-fin text-[0.7rem] text-[color:var(--br-text-dim)]">
                {duration === "open"
                  ? t("calc.chart_open_note")
                  : t("calc.chart_legend", {
                      months: projectionMonths,
                    })}
              </div>
            </div>
            <YieldChart
              principal={amount}
              netProfit={calc.netProfit}
              months={projectionMonths}
              chartMonths={projectionMonths}
              locale={locale}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4" data-testid="calc-numbers-grid">
            <Metric
              label={t("calc.rate_gross")}
              value={`${activeTier.gross_rate.toFixed(2)} %`}
              accent
              testid="calc-rate-gross"
            />
            <Metric
              label={t("calc.rate_net")}
              value={`${calc.netRate.toFixed(2)} %`}
              accent
              testid="calc-rate-net"
            />
            <Metric
              label={t("calc.mngmt_fee")}
              value={`${activeTier.mngmt_fee.toFixed(2)} %`}
              testid="calc-mngmt-fee"
            />
            <Metric
              label={t("calc.perf_fee")}
              value={`${activeTier.perf_fee.toFixed(2)} %`}
              testid="calc-perf-fee"
            />
            <Metric
              label={t("calc.lock_period")}
              value={lockLabel}
              testid="calc-lock-period"
            />
            <Metric
              label={t("calc.gross_profit", { months: projectionMonths })}
              value={formatCurrencyEUR(calc.grossProfit, locale)}
              testid="calc-gross-profit"
            />
            <Metric
              label={t("calc.net_profit", { months: projectionMonths })}
              value={formatCurrencyEUR(calc.netProfit, locale)}
              accent
              testid="calc-net-profit"
            />
            <Metric
              label={t("calc.maturity_value")}
              value={formatCurrencyEUR(calc.maturity, locale)}
              accent
              testid="calc-maturity"
            />
          </div>
          {activeTier.asset_allocation?.length ? (
            <div
              className="mt-8 pt-6"
              style={{ borderTop: "1px solid var(--br-line)" }}
            >
              <AssetAllocation
                allocation={activeTier.asset_allocation}
                volatilityTarget={activeTier.volatility_target}
                collapsible
              />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="px-8 pb-8 pt-4">
          <p
            className="text-[0.92rem] italic text-[color:var(--br-text-dim)]"
            data-testid="calc-no-plan"
          >
            {t("calc.no_plan")}
          </p>
        </div>
      )}

      <div
        className="relative px-8 py-4 text-[0.74rem] text-[color:var(--br-text-dim)] leading-relaxed"
        style={{
          borderTop: "1px solid var(--br-line)",
          background: "rgba(167,128,73,0.04)",
        }}
        data-testid="calc-disclaimer"
      >
        {t("calc.disclaimer")}
        <ul className="mt-3 space-y-1.5" data-testid="calc-eu-disclaimers">
          <li>· {t("disc.past")}</li>
          <li>· {t("disc.risk")}</li>
          <li>· {t("disc.mkt")}</li>
        </ul>
      </div>
    </section>
  );
}

function Metric({ label, value, accent = false, testid }) {
  return (
    <div className="br-card p-4" data-testid={testid}>
      <div className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase text-[color:var(--br-text-dim)]">
        {label}
      </div>
      <div
        className={`mt-2 ${accent ? "font-serif-display text-[1.5rem]" : "text-[1.05rem] font-mono-fin"}`}
        style={{ color: accent ? "var(--br-bronze)" : "var(--br-parchment)" }}
      >
        {value}
      </div>
    </div>
  );
}
